import { useState, useCallback } from 'react';
import { DashboardService } from '@/services/dashboard.service';

export function useDashboard(memberId?: string) {
  const service = memberId ? new DashboardService(memberId) : undefined;
  const [summary, setSummary] = useState<any[]>([]);
  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async (startYear?: number) => {
    setLoading(true);
    setError(null);
    try {
      if (!service) throw new Error('memberId required');
      const res = await service.getHealthStorySummary(startYear);
      setSummary(res || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch summary');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  const fetchDetails = useCallback(async (year: number, memberIdParam?: string) => {
    setLoading(true);
    setError(null);
    try {
      if (!service) throw new Error('memberId required');
      const res = await service.getHealthStoryDetails(year);
      setDetails(res || null);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch details');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  // Download emergency card PDF
  const downloadEmergencyCardPdf = useCallback(async () => {
    if (!memberId) throw new Error('memberId required');
    setLoading(true);
    setError(null);
    try {
      const blob = await import('@/services/emergency-info.service').then(mod => mod.emergencyInfoService.downloadEmergencyCardPdf(memberId));
      // Create a link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emergency-card-${memberId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Failed to download emergency card');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  return {
    summary,
    details,
    loading,
    error,
    fetchSummary,
    fetchDetails,
    downloadEmergencyCardPdf,
  };
}
